import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { EyeIcon, TrashBinIcon } from "../../icons"

// ── Types ──────────────────────────────────────────────────────────────────

type SeedbedStatus = "active" | "paused" | "archived"

type Institution = {
  id: string
  name: string
}

type Seedbed = {
  id: string
  name: string
  slug: string
  institutionId: string
  institution_name?: string
  status: SeedbedStatus
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  start_date?: string | null
  end_date?: string | null
  courses_count?: number
  students_count?: number
  groups_count?: number
}

// ── Helpers ────────────────────────────────────────────────────────────────

function toDateInput(value?: string | null): string {
  if (!value) return ""
  return value.slice(0, 10)
}

function statusBadge(status: SeedbedStatus): string {
  if (status === "active") return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
  if (status === "paused") return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
}

function statusLabelOf(status: SeedbedStatus): string {
  if (status === "active") return "Activo"
  if (status === "paused") return "Pausado"
  return "Archivado"
}

// ── Component ──────────────────────────────────────────────────────────────

export default function SeedbedsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [search, setSearch] = useState("")
  const [institutionFilter, setInstitutionFilter] = useState(searchParams.get("institution_id") ?? "")
  const [statusFilter, setStatusFilter] = useState<"" | SeedbedStatus>("")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [seedbedToDelete, setSeedbedToDelete] = useState<Seedbed | null>(null)  const {
    rows: seedbeds,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<Seedbed>({
    endpoint: "/academy/seedbeds/admin",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { search, institution_id: institutionFilter || undefined, status: statusFilter || undefined },
    extractRows: (data) => data.seedbeds ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin/institutions?limit=200&page=1")
      .then((res) => setInstitutions(res.data.data?.institutions ?? []))
      .catch(() => setInstitutions([]))
  }, [])

  // Metrics (over the current page — la API aún no expone conteos globales por estado)
  const activeCount = seedbeds.filter((s) => s.status === "active").length
  const pausedCount = seedbeds.filter((s) => s.status === "paused").length
  const archivedCount = seedbeds.filter((s) => s.status === "archived").length

  const onDelete = useCallback(
    async (id: string) => {
      setDeletingId(id)
      try {
        await api.delete(`/academy/seedbeds/admin/${id}`)
        toastSuccess("Semillero eliminado")
        await reload()
      } catch (err) {
        toastError(err)
      } finally {
        setDeletingId(null)
        setSeedbedToDelete(null)
      }
    },
    [reload]
  )

  const columns: DataTableColumn<Seedbed>[] = [
    {
      key: "name",
      label: "Semillero",
      sortable: true,
      render: (seedbed) => (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{seedbed.name}</p>
          <code className="text-xs text-gray-500 dark:text-gray-400">{seedbed.slug}</code>
        </>
      ),
    },
    {
      key: "institution",
      label: "Organización",
      render: (seedbed) => (
        <span className="text-gray-600 dark:text-gray-400">{seedbed.institution_name ?? "—"}</span>
      ),
    },
    {
      key: "status",
      label: "Estado",
      align: "center",
      render: (seedbed) => (
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${statusBadge(seedbed.status)}`}>
          {statusLabelOf(seedbed.status)}
        </span>
      ),
    },
    {
      key: "courses_count",
      label: "Cursos",
      align: "center",
      render: (seedbed) => <span className="text-gray-600 dark:text-gray-400">{seedbed.courses_count ?? 0}</span>,
    },
    {
      key: "students_count",
      label: "Estudiantes",
      align: "center",
      render: (seedbed) => <span className="text-gray-600 dark:text-gray-400">{seedbed.students_count ?? 0}</span>,
    },
    {
      key: "dates",
      label: "Fechas",
      render: (seedbed) => (
        <span className="text-xs text-gray-500">
          {toDateInput(seedbed.startDate ?? seedbed.start_date) || "—"}
          {(seedbed.endDate ?? seedbed.end_date) && <> → {toDateInput(seedbed.endDate ?? seedbed.end_date)}</>}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (seedbed) => (
        <RowActionsMenu
          actions={[
            { label: "Abrir / Editar", icon: <EyeIcon />, to: `/programs/seedbeds/${seedbed.id}` },
            {
              label: deletingId === seedbed.id ? "Eliminando..." : "Eliminar",
              icon: <TrashBinIcon />,
              onClick: () => setSeedbedToDelete(seedbed),
              variant: "danger",
              disabled: deletingId === seedbed.id,
              requires: "academy:seedbeds:edit",
            },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Programas", "Semilleros"]}
      title="Semilleros"
      subtitle="Programas de estudio organizados por organización"
      tabs={[{ label: "Semilleros", to: "/programs/seedbeds", active: true }]}
      metrics={[
        { label: "Total", value: String(total) },
        { label: "Activos (página)", value: String(activeCount), tone: "good" },
        {
          label: "Pausados (página)",
          value: String(pausedCount),
          tone: pausedCount > 0 ? "warning" : "neutral",
          hint: "Sin actividad: decide si reactivar o archivar",
        },
        { label: "Archivados (página)", value: String(archivedCount) },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar semillero...",
        },
        filters: [
          {
            type: "select",
            key: "institution",
            label: "Organización",
            value: institutionFilter,
            onChange: setInstitutionFilter,
            options: [
              { label: "Todas las organizaciones", value: "" },
              ...institutions.map((i) => ({ label: i.name, value: i.id })),
            ],
          },
          {
            type: "select",
            key: "status",
            label: "Estado",
            value: statusFilter,
            onChange: (value) => setStatusFilter(value as "" | SeedbedStatus),
            options: [
              { label: "Todos los estados", value: "" },
              { label: "Activo", value: "active" },
              { label: "Pausado", value: "paused" },
              { label: "Archivado", value: "archived" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate(`/programs/seedbeds/new${institutionFilter ? `?institution_id=${institutionFilter}` : ""}`),
        createLabel: "+ Nuevo semillero",
        requires: "academy:seedbeds:edit",
      }}
    >
      <DataTable
        columns={columns}
        rows={seedbeds}
        rowKey={(seedbed) => seedbed.id}
        loading={loading}
        emptyMessage="No se encontraron semilleros."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />      <ConfirmDialog
        isOpen={seedbedToDelete !== null}
        title={`¿Eliminar "${seedbedToDelete?.name ?? ""}"?`}
        message="Se eliminarán también todos sus grupos, inscripciones y asignaciones. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirming={deletingId === seedbedToDelete?.id}
        onConfirm={() => seedbedToDelete && void onDelete(seedbedToDelete.id)}
        onCancel={() => setSeedbedToDelete(null)}
      />
    </ModuleLayout>
  )
}
